import { Injectable } from '@angular/core';
import { UrlBuilderService } from '../services/url-builder-service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GitlabInstance } from '../model/gitlab-instance';

@Injectable({
  providedIn: 'root'
})
export class GitlabService {

  constructor(
    private urlBuilder: UrlBuilderService,
    private http: HttpClient) {
  }


  public getInstances(): Observable<GitlabInstance[]> {
    return this.http.get<GitlabInstance[]>(this.urlBuilder.buildUrl('/gitlab/instances'));
  }

  public getInstanceNames(): Observable<string[]> {
    return this.http.get<string[]>(this.urlBuilder.buildUrl('/gitlab/instances/names'));
  }

  public getRunners(gitlabInstance: string, activeOnly: boolean = false): Observable<any[]> {
    return this.http.get<any[]>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/runners', {name: gitlabInstance}),
      {params: {activeOnly: activeOnly ? 'true' : 'false'}});
  }

  public getMergeRequests(gitlabInstance: string): Observable<any[]> {
    return this.http.get<any[]>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/merge-requests', {name: gitlabInstance}));
  }

  public getRunnerJobs(gitlabInstance: string, runnerId: number, activeRunnersOnly: boolean = true): Observable<any[]> {
    return this.http.get<any[]>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/runners/{runnerId}/jobs', {
        name: gitlabInstance,
        runnerId: runnerId.toString()
      }),
      {params: {activeRunnersOnly: activeRunnersOnly ? 'true' : 'false'}});
  }


  public getProject(gitlabInstance: string, projectId: number): Observable<any> {
    return this.http.get<any>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/{projectId}', {
        name: gitlabInstance,
        projectId: projectId.toString()
      }));
  }


  public getPipeline(gitlabInstance: string, projectId: number, pipelineId: number): Observable<any> {
    return this.http.get<any>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/{projectId}/pipelines/{pipelineId}', {
        name: gitlabInstance,
        projectId: projectId.toString(),
        pipelineId: pipelineId.toString()
      }));
  }

  public getPipelines(gitlabInstance: string, projectId: number, branch: string): Observable<any[]> {
    return this.http.get<any[]>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/{projectId}/branches/{branch}/pipelines', {
        name: gitlabInstance,
        projectId: projectId.toString(),
        branch: branch
      }));
  }

  public getPipelineJobs(gitlabInstance: string, projectId: number, pipelineId: number): Observable<any[]> {
    return this.http.get<any[]>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/{projectId}/pipelines/{pipelineId}/jobs', {
        name: gitlabInstance,
        projectId: projectId.toString(),
        pipelineId: pipelineId.toString()
      }))
      .pipe(
        // fix job sorting (changed in recent gitlabs)
        map(jobs => jobs.sort((j1, j2) => j1.id - j2.id))
      );
  }


  public getPipelineTestReport(gitlabInstance: string, projectId: number, pipelineId: number): Observable<any> {
    return this.http.get<any>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/{projectId}/pipelines/{pipelineId}/test_report', {
        name: gitlabInstance,
        projectId: projectId.toString(),
        pipelineId: pipelineId.toString()
      }));
  }


  public getCommit(gitlabInstance: string, projectId: number, sha: string): Observable<any> {
    return this.http.get<any>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/{projectId}/commits/{sha}', {
        name: gitlabInstance,
        projectId: projectId.toString(),
        sha: sha
      }));
  }

  public trackProjectBranch(gitlabInstance: string, projectId: number, projectName: string, branch: string) {
    return this.http.post<void>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/branches/track', {
        name: gitlabInstance
      }), {projectId: projectId, projectName: projectName, branch: branch});
  }


  public untrackProjectBranch(gitlabInstance: string, projectId: number, projectName: string, branch: string) {
    return this.http.post<void>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/branches/untrack', {
        name: gitlabInstance
      }), {projectId: projectId, projectName: projectName, branch: branch});
  }

  public getTrackedProjectBranches(gitlabInstance: string) {
    return this.http.get<any[]>(
      this.urlBuilder.buildUrl('/gitlab/{name}/dashboard/projects/branches/track', {
        name: gitlabInstance
      }))
      .pipe(
        // apply sorting
        map(tracked => tracked.sort((t1, t2) =>
          t1.projectName.localeCompare(t2.projectName) || t1.branch.localeCompare(t2.branch)))
      );
  }
}
